import {
    getActiveHouseholdId,
    getActiveMembership,
    getMemberships,
    householdsApi,
    refreshMemberships,
    setActiveHousehold,
    type HouseholdDetail,
} from "../households";
import { getCurrentUser, logout } from "../auth";
import { showSheet } from "../components/modal";
import { showToast } from "../components/toast";
import { isNavigationStale } from "../navigation";

function escapeHtml(text: string): string {
    return text
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;");
}

function materialIcon(name: string, className = ""): string {
    return `<span class="material-symbols-outlined ${className}" aria-hidden="true">${name}</span>`;
}

export async function renderHouseholds(container: HTMLElement, gen: number): Promise<void> {
    const user = getCurrentUser();
    container.innerHTML = `
        <div class="hh-page hh-page--loading view-enter" aria-busy="true">
            <header class="hh-masthead">
                <div class="hh-sub-bar">
                    <div class="hh-shimmer hh-shimmer-eyebrow loading-shimmer cascade-glide" style="--stagger:0"></div>
                </div>
                <div class="hh-title-row">
                    <div class="hh-shimmer hh-shimmer-title loading-shimmer cascade-glide" style="--stagger:1"></div>
                </div>
            </header>
            <div class="hh-shimmer hh-shimmer-label loading-shimmer cascade-glide" style="--stagger:2"></div>
            <div class="hh-shimmer-row loading-shimmer cascade-glide" style="--stagger:3"></div>
            <div class="hh-shimmer-row loading-shimmer cascade-glide" style="--stagger:4"></div>
            <div class="hh-shimmer-row loading-shimmer cascade-glide" style="--stagger:5"></div>
            <div class="hh-shimmer-row hh-shimmer-row--settings loading-shimmer cascade-glide" style="--stagger:6"></div>
            <div class="hh-shimmer-row hh-shimmer-row--settings loading-shimmer cascade-glide" style="--stagger:7"></div>
        </div>`;
    await refreshMemberships();
    if (isNavigationStale(gen)) return;

    const memberships = getMemberships();
    const activeId = getActiveHouseholdId();

    let detail: HouseholdDetail | null = null;
    let incomingInvites: Awaited<ReturnType<typeof householdsApi.listInvites>> = [];
    if (activeId !== null && memberships.some((m) => m.id === activeId)) {
        try {
            detail = await householdsApi.get(activeId);
            if (isNavigationStale(gen)) return;
        } catch {
            /* fall through — show summary */
        }
    }
    try {
        incomingInvites = await householdsApi.listInvites();
        if (isNavigationStale(gen)) return;
    } catch {
        /* invites are non-critical */
    }

    const active = getActiveMembership();
    const isOwner = active?.role === "owner";

    container.innerHTML = `
        <div class="hh-page view-enter">
            <header class="hh-masthead">
                <div class="hh-sub-bar">
                    <span class="hh-eyebrow">Signed in as ${escapeHtml(user?.username ?? "")}</span>
                    <button id="hh-signout" type="button" class="hh-icon-btn" title="Sign out" aria-label="Sign out">
                        ${materialIcon("logout")}
                    </button>
                </div>
                <div class="hh-title-row" id="hh-title-row">
                    <button id="hh-switcher" type="button" class="hh-switcher">
                        <span class="hh-title font-display">${escapeHtml(active?.name ?? "Households")}</span>
                        ${materialIcon("expand_more", "hh-switcher-arrow")}
                    </button>
                </div>
            </header>

            ${
                memberships.length === 0
                    ? `<p class="hh-empty">No households yet — tap the title above to create or join one.</p>`
                    : ""
            }

            ${renderIncomingInvites(incomingInvites)}

            ${detail ? renderDetail(detail, isOwner, user?.id) : ""}
        </div>
    `;

    bindHandlers(container, detail, isOwner, gen);
}

function renderIncomingInvites(
    invites: { household_id: number; name: string; invited_by: string | null }[],
): string {
    if (invites.length === 0) return "";
    return `
        <section class="hh-section">
            <div class="hh-field">
                <span class="hh-field-label">Invitations</span>
                <ul class="hh-members">
                    ${invites
                        .map(
                            (inv) => `
                        <li class="hh-member">
                            <span class="hh-member-name">${escapeHtml(inv.name)}
                                ${inv.invited_by ? `<span class="hh-you">from ${escapeHtml(inv.invited_by)}</span>` : ""}
                            </span>
                            <span class="hh-member-actions">
                                <button type="button" class="hh-text-btn hh-invite-accept" data-household="${inv.household_id}">Accept</button>
                                <button type="button" class="hh-text-btn hh-text-btn--muted hh-invite-decline" data-household="${inv.household_id}">Decline</button>
                            </span>
                        </li>`,
                        )
                        .join("")}
                </ul>
            </div>
        </section>`;
}

function renderDetail(
    detail: HouseholdDetail,
    isOwner: boolean,
    userId: number | undefined,
): string {
    const selfIsOwner = detail.ownerId === userId;
    return `
        <section class="hh-section">
            <div class="hh-field">
                <div class="hh-field-head">
                    <span class="hh-field-label">Members</span>
                    <button id="hh-invite-btn" type="button" class="hh-icon-btn" title="Invite member" aria-label="Invite member">
                        ${materialIcon("person_add")}
                    </button>
                </div>
                <ul class="hh-members">
                    ${detail.members
                        .map(
                            (m) => `
                        <li class="hh-member">
                            <span class="hh-member-name">${escapeHtml(m.username)}${m.id === userId ? ' <span class="hh-you">you</span>' : ""}</span>
                            <span class="hh-member-actions">
                                ${m.isOwner ? '<span class="hh-badge">owner</span>' : ""}
                                ${
                                    isOwner && !m.isOwner
                                        ? `<button type="button" class="hh-icon-btn hh-transfer" data-member="${m.id}" title="Make owner" aria-label="Transfer ownership">${materialIcon("supervisor_account")}</button>
                                           <button type="button" class="hh-icon-btn hh-remove" data-member="${m.id}" title="Remove" aria-label="Remove member">${materialIcon("person_remove")}</button>`
                                        : ""
                                }
                            </span>
                        </li>`,
                        )
                        .join("")}
                    ${detail.pendingInvites
                        .map(
                            (p) => `
                        <li class="hh-member hh-member--pending">
                            <span class="hh-member-name">${escapeHtml(p.username)}</span>
                            <span class="hh-member-actions">
                                <span class="hh-badge hh-badge--muted">invited</span>
                                ${
                                    isOwner
                                        ? `<button type="button" class="hh-icon-btn hh-cancel-invite" data-member="${p.id}" title="Cancel invite" aria-label="Cancel invite">${materialIcon("close")}</button>`
                                        : ""
                                }
                            </span>
                        </li>`,
                        )
                        .join("")}
                </ul>
            </div>

            ${
                isOwner
                    ? `<button id="hh-rename-btn" type="button" class="hh-settings-row">
                           ${materialIcon("edit", "hh-form-icon")}
                           <span class="hh-settings-label">Rename household</span>
                           ${materialIcon("chevron_right", "hh-form-icon")}
                       </button>`
                    : ""
            }

            <button id="hh-ai-btn" type="button" class="hh-settings-row">
                ${materialIcon("tune", "hh-form-icon")}
                <span class="hh-settings-label">AI settings</span>
                <span class="hh-settings-hint">${detail.hasGeminiApiKey ? "household key" : "shared free key"}</span>
                ${materialIcon("chevron_right", "hh-form-icon")}
            </button>

            <div class="hh-danger">
                ${
                    selfIsOwner
                        ? `<button type="button" class="hh-text-btn hh-text-btn--danger" disabled title="Transfer ownership to another member first">Leave household</button>
                           <p class="hh-hint">Owners can't leave — transfer ownership to another member first.</p>`
                        : `<button id="hh-leave" type="button" class="hh-text-btn hh-text-btn--danger">Leave household</button>`
                }
            </div>
        </section>
    `;
}

function openSwitcherSheet(switchTo: (id: number, message: string) => void): void {
    const memberships = getMemberships();
    const activeId = getActiveHouseholdId();

    const content = document.createElement("div");
    content.className = "hh-sheet";
    content.innerHTML = `
        <div class="hh-sheet-list">
            ${memberships
                .map(
                    (m) => `
                <button type="button" class="hh-sheet-row" data-household="${m.id}">
                    <span class="hh-row-name">${escapeHtml(m.name)}</span>
                    <span class="hh-row-meta">${m.memberCount} member${m.memberCount === 1 ? "" : "s"} · ${m.role}</span>
                    ${materialIcon("check", `hh-sheet-check ${m.id === activeId ? "" : "hh-sheet-check--off"}`)}
                </button>`,
                )
                .join("")}
        </div>
        <div class="hh-sheet-actions">
            <form class="hh-inline-form" data-sheet="create">
                ${materialIcon("add", "hh-form-icon")}
                <input class="hh-input" type="text" placeholder="New household name" required maxlength="60" />
                <button type="submit" class="hh-text-btn">Create</button>
            </form>
            <form class="hh-inline-form" data-sheet="join">
                ${materialIcon("key", "hh-form-icon")}
                <input class="hh-input" type="text" placeholder="Invite code" required />
                <button type="submit" class="hh-text-btn">Join</button>
            </form>
        </div>
    `;

    const close = showSheet("Switch household", content, undefined, { dismissible: true });

    content.querySelectorAll<HTMLElement>(".hh-sheet-row").forEach((row) => {
        row.addEventListener("click", () => {
            const id = Number(row.dataset.household);
            close();
            if (!Number.isInteger(id) || id === activeId) return;
            switchTo(id, "Switched household.");
        });
    });

    content.querySelector('form[data-sheet="create"]')!.addEventListener("submit", (e) => {
        e.preventDefault();
        const input = (e.target as HTMLFormElement).querySelector("input")!;
        close();
        void householdsApi
            .create(input.value.trim())
            .then((h) => switchTo(h.id, `Created "${h.name}".`))
            .catch((err: Error) => showToast(err.message));
    });

    content.querySelector('form[data-sheet="join"]')!.addEventListener("submit", (e) => {
        e.preventDefault();
        const input = (e.target as HTMLFormElement).querySelector("input")!;
        close();
        void householdsApi
            .join(input.value.trim())
            .then((h) => switchTo(h.id, `Joined "${h.name}".`))
            .catch((err: Error) => showToast(err.message));
    });
}

function openAiSheet(detail: HouseholdDetail, isOwner: boolean, onChanged: () => void): void {
    const content = document.createElement("div");
    content.className = "hh-sheet";
    content.innerHTML = `
        <p class="hh-hint">
            ${
                detail.hasGeminiApiKey
                    ? `Using household key ${escapeHtml(detail.geminiApiKeyPreview ?? "")}.`
                    : "Using the shared free key."
            }
            If the household key fails, the free key is tried next, then plain text fallback.
            ${isOwner ? "" : " Only the owner can change this."}
        </p>
        ${
            isOwner
                ? `<form class="hh-sheet-form" data-ai="form">
                       <label class="hh-field-label" for="hh-ai-key">API key</label>
                       <input id="hh-ai-key" class="hh-input" type="password" placeholder="Paste Gemini API key" autocomplete="off" />
                       <label class="hh-field-label" for="hh-ai-model">Model</label>
                       <input id="hh-ai-model" class="hh-input" type="text" value="${escapeHtml(detail.geminiModel ?? "")}" placeholder="e.g. gemini-2.5-flash" />
                       <div class="hh-ai-actions">
                           <button type="submit" class="hh-text-btn">Save</button>
                           ${detail.hasGeminiApiKey ? '<button type="button" class="hh-text-btn hh-text-btn--muted" data-ai="clear">Clear key</button>' : ""}
                       </div>
                   </form>`
                : ""
        }
    `;

    const close = showSheet("AI settings", content, undefined, { dismissible: true });

    content.querySelector('form[data-ai="form"]')?.addEventListener("submit", (e) => {
        e.preventDefault();
        const form = e.target as HTMLFormElement;
        const key = form.querySelector<HTMLInputElement>("#hh-ai-key")!.value.trim();
        const model = form.querySelector<HTMLInputElement>("#hh-ai-model")!.value.trim();
        const payload: { geminiApiKey?: string | null; geminiModel?: string | null } = {
            geminiModel: model || null,
        };
        if (key) payload.geminiApiKey = key;
        close();
        void householdsApi
            .updateAiSettings(detail.id, payload)
            .then(() => {
                showToast("AI settings saved.");
                onChanged();
            })
            .catch((err: Error) => showToast(err.message));
    });

    content.querySelector('[data-ai="clear"]')?.addEventListener("click", () => {
        if (!confirm("Remove the household Gemini key? The shared free key will be used.")) return;
        close();
        void householdsApi
            .updateAiSettings(detail.id, { geminiApiKey: null })
            .then(() => {
                showToast("Household Gemini key removed.");
                onChanged();
            })
            .catch((err: Error) => showToast(err.message));
    });
}

function openInviteSheet(detail: HouseholdDetail, isOwner: boolean, onChanged: () => void): void {
    const inviteLink = `${location.origin}/?join=${encodeURIComponent(detail.inviteCode)}`;

    const content = document.createElement("div");
    content.className = "hh-sheet";
    content.innerHTML = `
        ${
            isOwner
                ? `<form class="hh-inline-form" data-invite="username">
                       ${materialIcon("person_add", "hh-form-icon")}
                       <input class="hh-input" type="text" placeholder="Add member by username" required />
                       <button type="submit" class="hh-text-btn">Add</button>
                   </form>`
                : ""
        }
        <div class="hh-sheet-actions">
            <button type="button" class="hh-sheet-row" data-invite="copy-code">
                ${materialIcon("key", "hh-form-icon")}
                <span class="hh-sheet-opt">
                    <span class="hh-row-name">Copy invite code</span>
                    <code class="hh-code hh-code--sm">${escapeHtml(detail.inviteCode)}</code>
                </span>
            </button>
            <button type="button" class="hh-sheet-row" data-invite="share-link">
                ${materialIcon("link", "hh-form-icon")}
                <span class="hh-sheet-opt">
                    <span class="hh-row-name">Share invite link</span>
                    <span class="hh-hint">They'll sign up or log in, then join automatically.</span>
                </span>
            </button>
        </div>
    `;

    const close = showSheet("Invite member", content, undefined, { dismissible: true });

    content.querySelector('form[data-invite="username"]')?.addEventListener("submit", (e) => {
        e.preventDefault();
        const input = (e.target as HTMLFormElement).querySelector("input")!;
        void householdsApi
            .addMember(detail.id, input.value.trim())
            .then(() => {
                close();
                showToast("Invitation sent.");
                onChanged();
            })
            .catch((err: Error) => showToast(err.message));
    });

    content.querySelector('[data-invite="copy-code"]')!.addEventListener("click", () => {
        void navigator.clipboard.writeText(detail.inviteCode).then(() => {
            close();
            showToast("Invite code copied.");
        });
    });

    content.querySelector('[data-invite="share-link"]')!.addEventListener("click", () => {
        close();
        if (navigator.share) {
            void navigator
                .share({ title: `Join ${detail.name} on Pantry Pal`, url: inviteLink })
                .catch(() => {});
        } else {
            void navigator.clipboard
                .writeText(inviteLink)
                .then(() => showToast("Invite link copied."));
        }
    });
}

function openRenameSheet(detail: HouseholdDetail, onRenamed: (name: string) => void): void {
    const content = document.createElement("div");
    content.className = "hh-sheet";
    content.innerHTML = `
        <form class="hh-sheet-form" data-rename="form">
            <label class="hh-field-label" for="hh-rename-input">Name</label>
            <input id="hh-rename-input" class="hh-input" type="text" value="${escapeHtml(detail.name)}" required maxlength="60" />
            <div class="hh-ai-actions">
                <button type="submit" class="hh-text-btn">Save</button>
            </div>
        </form>
    `;

    const close = showSheet("Rename household", content, undefined, { dismissible: true });
    const input = content.querySelector<HTMLInputElement>("#hh-rename-input")!;
    input.focus();
    input.select();

    content.querySelector('form[data-rename="form"]')!.addEventListener("submit", (e) => {
        e.preventDefault();
        const name = input.value.trim();
        close();
        if (!name || name === detail.name) return;
        // Optimistic: title updates instantly, the API confirms in the background.
        const title = document.querySelector<HTMLElement>("#hh-title-row .hh-title");
        if (title) title.textContent = name;
        const membership = getMemberships().find((m) => m.id === detail.id);
        if (membership) membership.name = name;
        onRenamed(name);
        void householdsApi
            .rename(detail.id, name)
            .then(() => showToast("Household renamed."))
            .catch((err: Error) => {
                showToast(err.message);
                location.reload();
            });
    });
}

function bindHandlers(
    container: HTMLElement,
    detail: HouseholdDetail | null,
    isOwner: boolean,
    gen: number,
): void {
    const user = getCurrentUser();
    const rerender = () => {
        const parent = container.closest("#app-main") as HTMLElement | null;
        if (parent && !isNavigationStale(gen)) void renderHouseholds(parent, gen);
    };
    const switchTo = (id: number, message: string) => {
        setActiveHousehold(id);
        showToast(message);
        location.hash = "#/";
        location.reload();
    };

    container.querySelector("#hh-switcher")?.addEventListener("click", () => {
        openSwitcherSheet(switchTo);
    });

    container.querySelector("#hh-rename-btn")?.addEventListener("click", () => {
        if (detail) {
            openRenameSheet(detail, (name) => {
                detail.name = name;
            });
        }
    });

    container.querySelectorAll<HTMLElement>(".hh-invite-accept").forEach((btn) => {
        btn.addEventListener("click", () => {
            const householdId = Number(btn.dataset.household);
            void householdsApi
                .acceptInvite(householdId)
                .then(async () => {
                    await refreshMemberships();
                    setActiveHousehold(householdId);
                    showToast("You joined the household.");
                    rerender();
                })
                .catch((err: Error) => showToast(err.message));
        });
    });

    container.querySelectorAll<HTMLElement>(".hh-invite-decline").forEach((btn) => {
        btn.addEventListener("click", () => {
            const householdId = Number(btn.dataset.household);
            void householdsApi
                .declineInvite(householdId)
                .then(() => {
                    showToast("Invitation declined.");
                    rerender();
                })
                .catch((err: Error) => showToast(err.message));
        });
    });

    container.querySelector("#hh-signout")?.addEventListener("click", () => {
        void logout();
    });

    if (!detail) return;
    const householdId = detail.id;

    container.querySelectorAll<HTMLElement>(".hh-cancel-invite").forEach((btn) => {
        btn.addEventListener("click", () => {
            const memberId = Number(btn.dataset.member);
            void householdsApi
                .cancelInvite(householdId, memberId)
                .then(() => {
                    showToast("Invitation cancelled.");
                    rerender();
                })
                .catch((err: Error) => showToast(err.message));
        });
    });

    container.querySelector("#hh-invite-btn")?.addEventListener("click", () => {
        openInviteSheet(detail, isOwner, rerender);
    });

    container.querySelectorAll<HTMLElement>(".hh-remove").forEach((btn) => {
        btn.addEventListener("click", () => {
            const memberId = Number(btn.dataset.member);
            if (!confirm("Remove this member?")) return;
            void householdsApi
                .removeMember(householdId, memberId)
                .then(() => {
                    showToast("Member removed.");
                    rerender();
                })
                .catch((err: Error) => showToast(err.message));
        });
    });

    container.querySelectorAll<HTMLElement>(".hh-transfer").forEach((btn) => {
        btn.addEventListener("click", () => {
            const memberId = Number(btn.dataset.member);
            if (!confirm("Transfer ownership? You will become a regular member.")) return;
            void householdsApi
                .transferOwnership(householdId, memberId)
                .then(() => {
                    showToast("Ownership transferred.");
                    rerender();
                })
                .catch((err: Error) => showToast(err.message));
        });
    });

    container.querySelector("#hh-ai-btn")?.addEventListener("click", () => {
        openAiSheet(detail, isOwner, rerender);
    });

    container.querySelector("#hh-leave")?.addEventListener("click", () => {
        if (user === null) return;
        if (!confirm(`Leave "${detail.name}"?`)) return;
        void householdsApi
            .leave(householdId, user.id)
            .then(() => {
                showToast("You left the household.");
                location.reload();
            })
            .catch((err: Error) => showToast(err.message));
    });
}
